/**
 * 見学予約: 入力 → 確認 → 完了（静的ビルド用 sessionStorage 連携）
 * 本番でサーバーがセッション／POST で確認画面を返す場合は、テンプレの data-visit-client-flow を外し form を通常送信にしてください。
 */
import { validateVisitReservationForm, initVisitFormValidation } from './visit-form-validation.js';

const STORAGE_KEY = 'takamatsu_visit_reservation_v1';
const VISIT_PREFILL_KEY = 'takamatsu_visit_prefill_v1';
const CONFIRM_PATH = '/visit_confirm.html';
const COMPLETE_PATH = '/visit_complete.html';

/** TODO 一時: デザイン確認用。本番では false に戻し、visit_confirm の #visit-confirm-block に hidden を付与 */
const VISIT_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

function getFormRoot() {
	return document.querySelector('form[data-visit-client-flow]#visit-reservation-form');
}

/** 確認画面用とは別に、修正に戻ったとき用の生フィールドを保存する */
function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	return {
		visit_date_1: pick('visit_date_1').trim(),
		visit_message: pick('visit_message'),
		visit_name_family: pick('visit_name_family').trim(),
		visit_name_given: pick('visit_name_given').trim(),
		visit_tel: pick('visit_tel').trim(),
		visit_email: pick('visit_email').trim(),
		visit_zip: pick('visit_zip').trim(),
		visit_pref: pick('visit_pref').trim(),
		visit_city: pick('visit_city').trim(),
		visit_street: pick('visit_street').trim(),
		visit_building: pick('visit_building').trim(),
		visit_privacy: fd.get('visit_privacy') === '1',
	};
}

function collectFormPayload(form) {
	const fd = new FormData(form);
	const visitPref = form.querySelector('#visit-pref');
	const prefLabel =
		visitPref && visitPref.selectedIndex >= 0
			? visitPref.options[visitPref.selectedIndex].text.trim()
			: '';
	const family = (fd.get('visit_name_family') || '').toString().trim();
	const given = (fd.get('visit_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '—';

	const zip = (fd.get('visit_zip') || '').toString().trim();
	const pref = (fd.get('visit_pref') || '').toString().trim();
	const city = (fd.get('visit_city') || '').toString().trim();
	const street = (fd.get('visit_street') || '').toString().trim();
	const building = (fd.get('visit_building') || '').toString().trim();
	const prefDisplay =
		prefLabel && prefLabel !== '都道府県' ? prefLabel : pref ? pref : '';
	const lines = [
		zip ? `〒${zip.replace(/^〒\s*/, '')}` : '',
		prefDisplay,
		city,
		street,
		building,
	].filter(Boolean);
	const addressFull = lines.length ? lines.join('\n') : '—';

	return {
		visit_date_1: (fd.get('visit_date_1') || '').toString().trim() || '—',
		visit_message: (fd.get('visit_message') || '').toString().trim() || '—',
		visit_name_full: nameFull,
		visit_tel: (fd.get('visit_tel') || '').toString().trim() || '—',
		visit_email: (fd.get('visit_email') || '').toString().trim() || '—',
		visit_address_full: addressFull,
		visit_privacy_agreed: fd.get('visit_privacy') === '1',
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore quota / private mode */
	}
}

const RAW_DATE_NAMES = ['visit_date_1'];

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	if (Object.prototype.hasOwnProperty.call(raw, 'visit_privacy')) {
		const cb = form.querySelector('input[name="visit_privacy"]');
		if (cb) cb.checked = Boolean(raw.visit_privacy);
	}

	for (const key of Object.keys(raw)) {
		if (key === 'visit_privacy') continue;
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;

		if (RAW_DATE_NAMES.includes(key) && str) {
			el.value = str;
			el.dispatchEvent(new Event('change', { bubbles: true }));
			continue;
		}

		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function initFormPage() {
	const form = getFormRoot();
	if (!form) return;

	initVisitFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		const restore = { ...draft._restore };
		// 物件詳細からの日付 prefill を優先（下書きの旧日付で上書きしない）
		if (sessionStorage.getItem(VISIT_PREFILL_KEY)) {
			delete restore.visit_date_1;
		}
		applyRestoreToForm(form, restore);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateVisitReservationForm(form)) {
			return;
		}
		const confirmPayload = collectFormPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...confirmPayload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function setTextContent(el, text) {
	if (!el) return;
	el.textContent = text;
}

/** --pre 用: 改行ごとにブロック化し、SCSS の gap:12px で行間を取る */
function setPreFormattedDesc(el, text) {
	if (!el) return;
	el.replaceChildren();
	const lines = String(text).split('\n');
	lines.forEach((line) => {
		const span = document.createElement('span');
		span.className = 'visit__confirmPreLine';
		span.textContent = line;
		el.appendChild(span);
	});
}

/** 日付値が YYYY-MM-DD の場合のみ YYYY/MM/DD に整形。日時文字列はそのまま表示 */
function formatConfirmDateDisplay(raw) {
	const s = String(raw).trim();
	if (s === '—' || !s) return s;
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
	if (m) return `${m[1]}/${m[2]}/${m[3]}`;
	return s;
}

function initConfirmPage() {
	const page = document.querySelector('[data-visit-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('visit-confirm-block');
	const empty = document.getElementById('visit-confirm-empty');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('visit-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (VISIT_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const hasRequired =
		draft.visit_date_1 !== '—' &&
		draft.visit_name_full !== '—' &&
		draft.visit_tel !== '—' &&
		draft.visit_email !== '—';

	if (!hasRequired) {
		if (VISIT_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	block.hidden = false;
	empty.hidden = true;

	page.querySelectorAll('[data-visit-field]').forEach((el) => {
		const key = el.getAttribute('data-visit-field');
		if (!key || !(key in draft)) return;
		const val = draft[key];
		let out = typeof val === 'boolean' ? (val ? '同意済み' : '—') : String(val);
		if (key.startsWith('visit_date_')) {
			out = formatConfirmDateDisplay(out);
		}
		if (el.classList.contains('visit__confirmDesc--pre')) {
			setPreFormattedDesc(el, out);
		} else {
			setTextContent(el, out);
		}
	});

	page.querySelectorAll('[data-visit-date-line]').forEach((line) => {
		const holder = line.querySelector('[data-visit-field]');
		if (holder && holder.textContent.trim() === '—') {
			line.hidden = true;
		}
	});
}

export function initVisitFlow() {
	const pageType = document.querySelector('.visit[data-visit-page]')?.getAttribute('data-visit-page');
	if (!pageType) return;
	if (pageType === 'form') initFormPage();
	if (pageType === 'confirm') initConfirmPage();
}
